import {
  Flex,
  Grid,
  GridItem,
  GridItemProps,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import {
  FundingRequestWithFullDetailsFragmentGQLType,
  GetBorrowingBaseReportQueryGQLType,
  useGetBorrowingBaseReportQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  dollarNumberWithCommas,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import { CalculationTerminologiesPopover } from 'packages/resources/ui/CalculationTerminologiesPopover';

const BorrowingBaseAvailabilityMiniBadge = ({
  isBorrowingBaseAvailable,
}: {
  isBorrowingBaseAvailable: boolean;
}) => {
  return (
    <Popover>
      <PopoverTrigger>
        <HStack gap="8px" cursor="pointer">
          <Icon
            icon={isBorrowingBaseAvailable ? 'circle-check' : 'circle-x'}
            color={
              isBorrowingBaseAvailable ? colors.green[400] : colors.red[400]
            }
          />
          <Text
            color={
              isBorrowingBaseAvailable ? colors.green[400] : colors.red[400]
            }
          >
            {isBorrowingBaseAvailable ? 'Passed' : 'Failed'}
          </Text>
        </HStack>
      </PopoverTrigger>
      <PopoverContent
        bgColor={colors.gray[900]}
        color="white"
        p="16px"
        textAlign="center"
      >
        <Text>{`BorrowingBaseAvailability > 0`}</Text>
      </PopoverContent>
    </Popover>
  );
};

const FundingImpactOverviewRowShell = ({
  label,
  before,
  after,
  lastCol,
  labelColStyle,
  beforeColStyle,
  afterColStyle,
  lastColStyle,
}: {
  label: React.ReactNode;
  before: React.ReactNode;
  after?: React.ReactNode;
  lastCol?: React.ReactNode;
  labelColStyle?: GridItemProps['style'];
  beforeColStyle?: GridItemProps['style'];
  afterColStyle?: GridItemProps['style'];
  lastColStyle?: GridItemProps['style'];
}) => {
  return (
    <>
      <GridItem
        colSpan={5}
        display="flex"
        alignItems="center"
        style={labelColStyle}
      >
        {label}
      </GridItem>
      <GridItem
        colSpan={4}
        display="flex"
        alignItems="center"
        justifyContent={after ? 'flex-end' : undefined}
        style={beforeColStyle}
      >
        {before}
      </GridItem>
      {after && (
        <GridItem colSpan={4} style={afterColStyle}>
          {after}
        </GridItem>
      )}
      <GridItem
        colSpan={1}
        display="flex"
        alignItems="center"
        style={lastColStyle}
      >
        {lastCol}
      </GridItem>
    </>
  );
};

const TopLevelFundingImpactOverviewRow = ({
  label,
  before,
  after,
  lastCol,
}: {
  label: React.ReactNode;
  before: React.ReactNode;
  after?: React.ReactNode;
  lastCol?: React.ReactNode;
}) => {
  return (
    <FundingImpactOverviewRowShell
      label={
        <>
          <Icon icon="circle-dollar" size="16px" />
          {label}
        </>
      }
      before={before}
      after={after}
      lastCol={lastCol}
      labelColStyle={{
        borderBottom: '2px solid',
        borderColor: colors.gray[50],
        color: colors.black[95],
        gap: '16px',
        padding: '16px',
      }}
      beforeColStyle={{
        borderBottom: '2px solid',
        borderColor: colors.gray[50],
        color: colors.black[95],
        padding: '16px',
      }}
      afterColStyle={{
        borderBottom: '2px solid',
        borderColor: colors.gray[50],
        backgroundColor: colors.aurora[900],
        color: 'white',
        padding: '16px',
      }}
    />
  );
};

const IndentedFundingImpactOverviewRow = ({
  indentPadding,
  label,
  before,
  after,
  lastCol,
}: {
  label: React.ReactNode;
  before: React.ReactNode;
  after?: React.ReactNode;
  lastCol?: React.ReactNode;
  indentPadding?: React.CSSProperties['padding'];
}) => {
  return (
    <FundingImpactOverviewRowShell
      label={label}
      before={before}
      after={after}
      lastCol={lastCol}
      labelColStyle={{
        color: colors.black[95],
        gap: '16px',
        padding: `0px ${indentPadding || '16px'}`,
      }}
      beforeColStyle={{
        color: colors.black[70],
        padding: '16px',
      }}
      afterColStyle={{
        padding: '16px',
      }}
    />
  );
};

export const IndentedHighlightedFundingImpactOverviewRow = ({
  indentPadding,
  label,
  before,
  after,
  lastCol,
}: {
  label: React.ReactNode;
  before: React.ReactNode;
  after?: React.ReactNode;
  lastCol?: React.ReactNode;
  indentPadding?: React.CSSProperties['padding'];
}) => {
  return (
    <FundingImpactOverviewRowShell
      label={label}
      before={before}
      after={after}
      lastCol={lastCol}
      labelColStyle={{
        color: colors.black[95],
        gap: '16px',
        padding: `0px ${indentPadding || '16px'}`,
        backgroundColor: colors.aurora[50],
      }}
      beforeColStyle={{
        color: colors.black[70],
        backgroundColor: colors.aurora[50],
        padding: '16px',
      }}
      afterColStyle={{
        backgroundColor: colors.aurora[900],
        color: 'white',
        padding: '16px',
      }}
    />
  );
};

const TotalBorrowingBaseFundingImpactRow = ({
  initialBorrowingBaseReport,
  proformaBorrowingBaseReport,
}: {
  initialBorrowingBaseReport: GetBorrowingBaseReportQueryGQLType['borrowingBaseReport'];
  proformaBorrowingBaseReport?: FundingRequestWithFullDetailsFragmentGQLType['borrowingBaseReport'];
}) => {
  const [
    isTotalBorrowingBaseOpen,
    setIsTotalBorrowingBaseOpen,
  ] = useState<boolean>(false);

  const totalBorrowingBaseAmountDerivedBefore =
    (initialBorrowingBaseReport.maxReceivableAdvanceAmount ?? 0) +
    (initialBorrowingBaseReport.totalCashBorrowingBaseAmount ?? 0);

  const totalBorrowingBaseAmountDerivedAfter =
    (proformaBorrowingBaseReport?.maxReceivableAdvanceAmount ?? 0) +
    (proformaBorrowingBaseReport?.totalCashBorrowingBaseAmount ?? 0);

  return (
    <>
      <TopLevelFundingImpactOverviewRow
        label={<Text>Total Borrowing Base</Text>}
        before={dollarNumberWithCommas(totalBorrowingBaseAmountDerivedBefore)}
        after={
          proformaBorrowingBaseReport
            ? dollarNumberWithCommas(totalBorrowingBaseAmountDerivedAfter)
            : undefined
        }
        lastCol={
          <IconButton
            aria-label={
              isTotalBorrowingBaseOpen
                ? 'hide borrowing base breakdown'
                : 'show borrowing base breakdown'
            }
            icon={
              <Icon
                icon={
                  isTotalBorrowingBaseOpen
                    ? 'circle-chevron-up'
                    : 'circle-chevron-down'
                }
                size="24px"
              />
            }
            onClick={() =>
              setIsTotalBorrowingBaseOpen((oldIsOpen) => !oldIsOpen)
            }
            variant="ghost"
            h="100%"
            w="100%"
            _hover={{ backgroundColor: 'none' }}
            _focus={{ backgroundColor: 'none' }}
            _active={{ backgroundColor: 'none' }}
          />
        }
      />
      {isTotalBorrowingBaseOpen && (
        <>
          <IndentedFundingImpactOverviewRow
            label="Borrowing Base from Receivables"
            before={dollarNumberWithCommas(
              initialBorrowingBaseReport.maxReceivableAdvanceAmount,
            )}
            after={
              proformaBorrowingBaseReport
                ? dollarNumberWithCommas(
                    proformaBorrowingBaseReport.maxReceivableAdvanceAmount,
                  )
                : undefined
            }
            indentPadding="32px"
          />
          <IndentedFundingImpactOverviewRow
            label="Borrowing Base from Cash"
            before={dollarNumberWithCommas(
              initialBorrowingBaseReport.totalCashBorrowingBaseAmount ?? 0,
            )}
            after={
              proformaBorrowingBaseReport
                ? dollarNumberWithCommas(
                    proformaBorrowingBaseReport.totalCashBorrowingBaseAmount ??
                      0,
                  )
                : undefined
            }
            indentPadding="32px"
          />
        </>
      )}
    </>
  );
};

export const FundingImpact = ({
  borrowingBaseReportId,
  proformaBorrowingBaseReport,
  displayPrincipalBalance,
}: {
  borrowingBaseReportId: string;
  proformaBorrowingBaseReport?: FundingRequestWithFullDetailsFragmentGQLType['borrowingBaseReport'];
  displayPrincipalBalance: boolean;
}) => {
  const [
    isBorrowingBaseAvailabilityOpen,
    setIsBorrowingBaseAvailabilityOpen,
  ] = useState<boolean>(false);

  const {
    data: borrowingBaseReportData,
    loading: loadingBorrowingBaseReportData,
  } = useGetBorrowingBaseReportQuery({
    variables: { borrowingBaseReportId },
  });

  if (!borrowingBaseReportData || loadingBorrowingBaseReportData) {
    return (
      <>
        <Grid
          templateColumns={`repeat(${
            proformaBorrowingBaseReport ? 14 : 10
          }, 1fr)`}
          templateRows="repeat(1, 56px)"
          rowGap="16px"
          color={colors.black[95]}
        >
          <GridItem colSpan={5} />
          <GridItem
            colSpan={4}
            borderBottom="1px solid"
            borderColor={colors.gray[50]}
            alignSelf="end"
            p="16px"
            fontWeight="600"
          >
            {proformaBorrowingBaseReport ? 'Before' : 'Amount'}
          </GridItem>
          {proformaBorrowingBaseReport && (
            <GridItem
              colSpan={4}
              borderBottom="1px solid"
              borderColor={colors.gray[50]}
              alignSelf="end"
              bg={colors.aurora[900]}
              color="white"
              fontWeight="600"
              p="16px"
            >
              After
            </GridItem>
          )}
          <GridItem
            colSpan={1}
            borderBottom="1px solid"
            borderColor={colors.gray[50]}
          />
          <TopLevelFundingImpactOverviewRow
            label={<Text>Commitment</Text>}
            before={<Skeleton w="100%" />}
            after={
              proformaBorrowingBaseReport ? <Skeleton w="100%" /> : undefined
            }
          />
          {displayPrincipalBalance && (
            <TopLevelFundingImpactOverviewRow
              label={<Text>Principal Balance</Text>}
              before={<Skeleton w="100%" />}
              after={
                proformaBorrowingBaseReport ? <Skeleton w="100%" /> : undefined
              }
            />
          )}
          <TopLevelFundingImpactOverviewRow
            label={<Text>Collections Account Balance</Text>}
            before={<Skeleton w="100%" />}
            after={
              proformaBorrowingBaseReport ? <Skeleton w="100%" /> : undefined
            }
          />
          <TopLevelFundingImpactOverviewRow
            label={<Text>Required Reserve</Text>}
            before={<Skeleton w="100%" />}
            after={
              proformaBorrowingBaseReport ? <Skeleton w="100%" /> : undefined
            }
          />
          <TopLevelFundingImpactOverviewRow
            label={<Text>Total Borrowing Base</Text>}
            before={<Skeleton w="100%" />}
            after={
              proformaBorrowingBaseReport ? <Skeleton w="100%" /> : undefined
            }
          />
          <TopLevelFundingImpactOverviewRow
            label={<Text>Maximum Availability</Text>}
            before={<Skeleton w="100%" />}
            after={
              proformaBorrowingBaseReport ? <Skeleton w="100%" /> : undefined
            }
          />
          <TopLevelFundingImpactOverviewRow
            label={<Text>Borrowing Base Availability</Text>}
            before={<Skeleton w="100%" />}
            after={
              proformaBorrowingBaseReport ? <Skeleton w="100%" /> : undefined
            }
          />
        </Grid>
      </>
    );
  }

  const {
    borrowingBaseReport: initialBorrowingBaseReport,
  } = borrowingBaseReportData;

  return (
    <>
      <Grid
        templateColumns={`repeat(${
          proformaBorrowingBaseReport ? 14 : 10
        }, 1fr)`}
        templateRows="repeat(1, 56px)"
        rowGap="16px"
        color={colors.black[95]}
      >
        <GridItem colSpan={5} />
        <GridItem
          colSpan={4}
          borderBottom="1px solid"
          borderColor={colors.gray[50]}
          alignSelf="end"
          p="16px"
          fontWeight="600"
        >
          {proformaBorrowingBaseReport ? 'Before' : 'Amount'}
        </GridItem>
        {proformaBorrowingBaseReport && (
          <GridItem
            colSpan={4}
            borderBottom="1px solid"
            borderColor={colors.gray[50]}
            alignSelf="end"
            bg={colors.aurora[900]}
            color="white"
            fontWeight="600"
            p="16px"
          >
            After
          </GridItem>
        )}
        <GridItem
          colSpan={1}
          borderBottom="1px solid"
          borderColor={colors.gray[50]}
        />
        <TopLevelFundingImpactOverviewRow
          label={<Text>Commitment</Text>}
          before={dollarNumberWithCommas(
            initialBorrowingBaseReport.commitmentAmount,
          )}
          after={
            proformaBorrowingBaseReport
              ? dollarNumberWithCommas(
                  proformaBorrowingBaseReport.commitmentAmount,
                )
              : undefined
          }
        />
        {displayPrincipalBalance && (
          <TopLevelFundingImpactOverviewRow
            label={<Text>Principal Balance</Text>}
            before={dollarNumberWithCommas(
              initialBorrowingBaseReport.currentPrincipalBalance,
            )}
            after={
              proformaBorrowingBaseReport
                ? dollarNumberWithCommas(
                    proformaBorrowingBaseReport.currentPrincipalBalance,
                  )
                : undefined
            }
          />
        )}
        <TopLevelFundingImpactOverviewRow
          label={<Text>Collections Account Balance</Text>}
          before={dollarNumberWithCommas(
            initialBorrowingBaseReport.totalCollectionsAccountCash ?? 0,
          )}
          after={
            proformaBorrowingBaseReport
              ? dollarNumberWithCommas(
                  proformaBorrowingBaseReport.totalCollectionsAccountCash ?? 0,
                )
              : undefined
          }
        />
        <TopLevelFundingImpactOverviewRow
          label={<Text>Required Reserve</Text>}
          before={dollarNumberWithCommas(
            initialBorrowingBaseReport.borrowingBaseReserveAmount ?? 0,
          )}
          after={
            proformaBorrowingBaseReport
              ? dollarNumberWithCommas(
                  proformaBorrowingBaseReport.borrowingBaseReserveAmount ?? 0,
                )
              : undefined
          }
        />
        <TotalBorrowingBaseFundingImpactRow
          initialBorrowingBaseReport={initialBorrowingBaseReport}
          proformaBorrowingBaseReport={proformaBorrowingBaseReport}
        />
        <TopLevelFundingImpactOverviewRow
          label={
            <HStack w="100%" alignItems="center" gap="16px">
              <Text>Maximum Available Amount</Text>
              <CalculationTerminologiesPopover termKeyLabel="maximumAvailableAmount" />
            </HStack>
          }
          before={dollarNumberWithCommas(
            initialBorrowingBaseReport.maximumAvailableAmount ?? 0,
          )}
          after={
            proformaBorrowingBaseReport
              ? dollarNumberWithCommas(
                  proformaBorrowingBaseReport.maximumAvailableAmount ?? 0,
                )
              : undefined
          }
        />
        <TopLevelFundingImpactOverviewRow
          label={
            <Flex w="100%" gap="16px" alignItems="center">
              <Text>Borrowing Base Availability</Text>
              <CalculationTerminologiesPopover termKeyLabel="borrowingBaseAvailability" />
              <BorrowingBaseAvailabilityMiniBadge
                isBorrowingBaseAvailable={
                  proformaBorrowingBaseReport
                    ? typeof proformaBorrowingBaseReport.borrowingBaseAvailability ===
                        'number' &&
                      proformaBorrowingBaseReport.borrowingBaseAvailability > 0
                    : typeof initialBorrowingBaseReport.borrowingBaseAvailability ===
                        'number' &&
                      initialBorrowingBaseReport.borrowingBaseAvailability > 0
                }
              />
            </Flex>
          }
          before={dollarNumberWithCommas(
            initialBorrowingBaseReport.borrowingBaseAvailability ?? 0,
          )}
          after={
            proformaBorrowingBaseReport
              ? dollarNumberWithCommas(
                  proformaBorrowingBaseReport.borrowingBaseAvailability ?? 0,
                )
              : undefined
          }
          lastCol={
            <IconButton
              icon={
                <Icon
                  icon={
                    isBorrowingBaseAvailabilityOpen
                      ? 'circle-chevron-up'
                      : 'circle-chevron-down'
                  }
                  size="24px"
                />
              }
              aria-label={
                isBorrowingBaseAvailabilityOpen
                  ? 'hide borrowing base availability rate'
                  : 'show borrowing base availability rate'
              }
              onClick={() =>
                setIsBorrowingBaseAvailabilityOpen((isOpen) => !isOpen)
              }
              variant="ghost"
              h="100%"
              w="100%"
              _hover={{ backgroundColor: 'none' }}
              _focus={{ backgroundColor: 'none' }}
              _active={{ backgroundColor: 'none' }}
            />
          }
        />
        {isBorrowingBaseAvailabilityOpen && (
          <IndentedFundingImpactOverviewRow
            indentPadding="32px"
            label={
              <HStack w="100%" alignItems="center" gap="16px">
                <Text>Borrowing Base Availability Rate</Text>
                <CalculationTerminologiesPopover termKeyLabel="borrowingBaseAvailabilityRate" />
              </HStack>
            }
            before={percentageToPercentageLabel(
              initialBorrowingBaseReport.borrowingBaseAvailabilityRate ?? 0,
              2,
            )}
            after={
              proformaBorrowingBaseReport
                ? percentageToPercentageLabel(
                    proformaBorrowingBaseReport.borrowingBaseAvailabilityRate ??
                      0,
                    2,
                  )
                : undefined
            }
          />
        )}
      </Grid>
    </>
  );
};
